import { Typography, Row, Col, Collapse } from "antd"
import React, { FC } from "react"
import { useTernaryDarkMode } from "usehooks-ts"
import "./index.less"
const { Title, Paragraph, Text } = Typography
const { Panel } = Collapse

const PrivacyPolicyContent: FC = () => {
  const { isDarkMode } = useTernaryDarkMode()
  
  return (
    <div className={`privacy-policy-content ${isDarkMode ? 'dark' : 'light'}`}>
        <Row className="privacy-policy-header">
          <Col span={24}>
            <span className="effective-date">Last Updated: October 8, 2026</span>
            <Title level={2}>Privacy Policy</Title>
            <Paragraph className="subtitle">
              At Datacord LLC, we are committed to protecting your privacy and handling your data with transparency 
              and integrity. This Privacy Policy explains how we collect, use, share, and safeguard your information 
              when you use our integration platform as a service (iPaaS).
            </Paragraph>
          </Col>
        </Row>
        
        <Row gutter={[0, 16]} className="privacy-policy-sections">
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Privacy Policy Overview</Title>
              <div className="policy-content">
                <Paragraph>
                  This Privacy Policy applies to Datacord LLC ("Datacord," "we," "us," or "our") and explains how information you provide to us or that we obtain from your use of our platform, website, products, and services is collected, used, shared, and protected.
                </Paragraph>
                <Paragraph>
                  Our privacy practices outlined here reflect Datacord's activities as both a data controller and data processor. This Privacy Policy applies to the Datacord website, platform, and services, and does not apply to any third-party services or business partners, even if links to their websites are found within our services. We recommend reviewing the privacy statements of other parties with whom you interact.
                </Paragraph>
                <Paragraph>
                  For purposes of this Privacy Policy, "Business Partners" means subcontractors, vendors, or other entities with whom we have ongoing business relationships to provide products, services, or information. "Personal Information" means information that can identify you, such as your name, telephone number, email address, Internet Protocol ("IP") address, and location data.
                </Paragraph>
                <Paragraph>
                  By using our services or otherwise interacting with our business, you acknowledge that you understand and agree to the terms of this Privacy Policy. If you do not agree with this Policy, please do not use our services.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Information We Collect</Title>
              <div className="policy-content">
                <Paragraph>
                  We collect information to provide better services to all our users and customers. The information Datacord collects, and how that information is used, depends on how you use our services and how you manage your privacy controls.
                </Paragraph>
                
                <Title level={4}>Information You Provide to Us</Title>
                <Paragraph>
                  When you create a Datacord account, purchase a subscription, or contact us for support, you provide us with personal information that includes your name, email address, and in some cases, billing information. If you use our services through your organization, your administrator may provide us with your contact details and account access information.
                </Paragraph>
                <ul>
                  <li><Text strong>Account and Profile Information:</Text> We collect information such as your name, email address, password, company name, job title, and other details you provide when creating an account or profile.</li>
                  <li><Text strong>Billing Information:</Text> When you purchase our services, we collect payment information, billing addresses, and details about your subscription plan and payments.</li>
                  <li><Text strong>Communications:</Text> We collect information you provide when you contact us for support, participate in surveys, or communicate with us in any way.</li>
                </ul>
                
                <Title level={4}>Information We Collect Automatically</Title>
                <Paragraph>
                  When you use our platform and services, we automatically collect certain information:
                </Paragraph>
                <ul>
                  <li><Text strong>Usage Information:</Text> We collect information about how you use our platform, including the features you access, actions you take, timestamps, frequency of use, and performance data.</li>
                  <li><Text strong>Device and Connection Information:</Text> We collect device-specific information such as your hardware model, operating system version, browser type and version, IP address, and device identifiers.</li>
                  <li><Text strong>Cookies and Similar Technologies:</Text> We use cookies, web beacons, pixel tags, and similar technologies to recognize you, customize your experience, and analyze usage patterns. For more information, please see our Cookie Policy.</li>
                  <li><Text strong>Log Information:</Text> Our servers automatically record information when you use our services, including access times, pages viewed, features used, and any errors encountered.</li>
                </ul>
                <Paragraph>
                  When you visit or log in to our website, cookies and similar technologies may be used by our online data partners or vendors to associate these activities with other personal information they or others have about you, including by association with your email. We (or service providers on our behalf) may then send communications and marketing to these email addresses. You may opt out of receiving this advertising by visiting <a href="https://app.retention.com/optout">https://app.retention.com/optout</a>.
                </Paragraph>
                
                <Title level={4}>Information About Your Data Integrations</Title>
                <Paragraph>
                  As an integration platform, Datacord processes data flowing between systems:
                </Paragraph>
                <ul>
                  <li><Text strong>Integration Metadata:</Text> We collect metadata about your integrations, including connector configurations, API endpoints, mapping rules, and scheduling information.</li>
                  <li><Text strong>Transaction Logs:</Text> We maintain logs of integration execution, including timestamps, status information, and error messages.</li>
                  <li><Text strong>Transient Data:</Text> During the process of integrating your applications, data may temporarily pass through our systems to complete the requested operations.</li>
                </ul>
                <Paragraph>
                  <Text strong>Important:</Text> Datacord is designed to minimize data persistence. While data flows through our platform during integrations, we do not persistently store the content of your data transfers unless specifically configured to do so for troubleshooting, auditing, or similar necessary purposes.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">How We Use Your Information</Title>
              <div className="policy-content">
                <Paragraph>
                  We use the information we collect for the following purposes:
                </Paragraph>
                <ul>
                  <li><Text strong>Providing and Maintaining Our Services:</Text> To deliver the Datacord platform and services, process transactions, fulfill subscription requirements, and provide technical support.</li>
                  <li><Text strong>Improving Our Services:</Text> To develop new features, enhance existing functionality, fix bugs, and optimize performance based on how users interact with our platform.</li>
                  <li><Text strong>Account Management:</Text> To create and maintain your account, authenticate users, process payments, and provide customer support.</li>
                  <li><Text strong>Communications:</Text> To communicate with you about your account, respond to inquiries, provide technical support, send service updates, security alerts, and administrative messages.</li>
                  <li><Text strong>Marketing and Promotions:</Text> To send promotional communications about new features, offers, and events (with your consent where required by law). You can opt-out of these communications at any time.</li>
                  <li><Text strong>Security and Fraud Prevention:</Text> To detect, prevent, and address security incidents, protect against harmful or illegal activity, and maintain the integrity of our platform.</li>
                  <li><Text strong>Legal Compliance:</Text> To comply with applicable laws, regulations, legal processes, or enforceable governmental requests.</li>
                </ul>
                <Paragraph>
                  We process your personal information for these purposes based on the following legal grounds:
                </Paragraph>
                <ul>
                  <li>Performance of our contract with you</li>
                  <li>Our legitimate business interests</li>
                  <li>Compliance with our legal obligations</li>
                  <li>Your consent, where applicable</li>
                </ul>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">How We Share Your Information</Title>
              <div className="policy-content">
                <Paragraph>
                  Datacord does not sell your personal information. We share your information in the following limited circumstances:
                </Paragraph>
                <ul>
                  <li><Text strong>With Your Consent:</Text> We will share your information when you have explicitly agreed to the disclosure.</li>
                  <li><Text strong>With Service Providers:</Text> We work with trusted third-party service providers who perform services on our behalf, such as cloud hosting, payment processing, email delivery, customer support, and analytics. These providers have access to your information only to perform these tasks and are contractually obligated to protect your data.</li>
                  <li><Text strong>For Legal Reasons:</Text> We may disclose information if we believe in good faith that disclosure is necessary to (a) comply with applicable laws, regulations, legal processes, or governmental requests; (b) protect the rights, property, and safety of Datacord, our users, or the public; (c) detect, prevent, or address fraud, security, or technical issues; or (d) enforce our Terms of Service, including investigating potential violations.</li>
                  <li><Text strong>Business Transfers:</Text> If Datacord is involved in a merger, acquisition, or sale of all or a portion of its assets, your information may be transferred as part of that transaction. We will notify you of any such change in ownership or control of your personal information.</li>
                  <li><Text strong>Aggregated or De-identified Information:</Text> We may share aggregated or de-identified information that cannot reasonably be used to identify you with third parties for industry analysis, research, and similar purposes.</li>
                </ul>
                <Paragraph>
                  Mobile information collected for text messaging, including your mobile number and SMS opt-in consent, will not be shared with third parties or affiliates for their own marketing.
                </Paragraph>
                <Paragraph>
                  When we share your information with our service providers (subprocessors), we ensure they provide at least the same level of data protection as described in this Privacy Policy through appropriate contractual agreements.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Security & Storage</Title>
              <div className="policy-content">
                <Paragraph>
                  Protecting your information is a top priority for Datacord. We implement robust security measures to safeguard your personal information and the data processed through our platform:
                </Paragraph>
                <ul>
                  <li><Text strong>Encryption:</Text> We use industry-standard encryption technologies (TLS/SSL) to protect data in transit. Data at rest is encrypted using AES-256 encryption.</li>
                  <li><Text strong>Access Controls:</Text> We maintain strict access controls limiting employee access to customer data based on job function and role.</li>
                  <li><Text strong>Authentication:</Text> We support multi-factor authentication and single sign-on options to secure access to your Datacord account.</li>
                  <li><Text strong>Infrastructure Security:</Text> Our infrastructure includes firewalls, intrusion detection systems, and regular security audits to protect against unauthorized access.</li>
                  <li><Text strong>Regular Testing:</Text> We conduct regular vulnerability assessments and penetration testing to identify and address potential security weaknesses.</li>
                  <li><Text strong>Incident Response:</Text> We have a comprehensive incident response plan to quickly address any potential security issues.</li>
                </ul>
                <Paragraph>
                  While we implement safeguards designed to protect your information, no security system is impenetrable. We cannot guarantee the security of information transmitted to us over the internet, and we are not responsible for the actions of third parties who may receive your information.
                </Paragraph>
                <Paragraph>
                  Datacord hosts its services in secure, SOC 2 compliant data centers. Our primary data storage is located in the United States, with additional regional storage options available for customers with specific data residency requirements.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Your Privacy Rights</Title>
              <div className="policy-content">
                <Paragraph>
                  Depending on your location, you may have certain rights regarding your personal information. These may include:
                </Paragraph>
                <ul>
                  <li><Text strong>Access:</Text> You can request information about the personal data we hold about you and how it's used.</li>
                  <li><Text strong>Correction:</Text> You can request that we correct inaccurate or incomplete information about you.</li>
                  <li><Text strong>Deletion:</Text> You can request that we delete your personal information in certain circumstances.</li>
                  <li><Text strong>Data Portability:</Text> You can request a copy of certain personal information in a machine-readable format.</li>
                  <li><Text strong>Restriction and Objection:</Text> You can request that we limit how we use your data or object to certain processing of your data.</li>
                  <li><Text strong>Withdrawing Consent:</Text> Where processing is based on your consent, you can withdraw that consent at any time.</li>
                  <li><Text strong>Complaint:</Text> You have the right to lodge a complaint with a supervisory authority.</li>
                </ul>
                <Paragraph>
                  To exercise these rights, please contact us at privacy@datacord.com. We will respond to your request within the timeframe required by applicable law (typically 30 days). We may need to verify your identity before processing your request.
                </Paragraph>
                <Paragraph>
                  For California residents, please note that we do not sell personal information as defined by the California Consumer Privacy Act (CCPA). For more information about your rights under the CCPA, please see our California Privacy Rights section.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">International Data Transfers</Title>
              <div className="policy-content">
                <Paragraph>
                  Datacord is headquartered in the United States, and we may transfer, store, and process your information in countries other than your country of residence. These countries may have data protection laws that are different from those in your country.
                </Paragraph>
                <Paragraph>
                  When we transfer personal information outside of the European Economic Area (EEA), United Kingdom, or Switzerland, we ensure that adequate safeguards are in place to protect your information and comply with applicable data protection laws. These safeguards may include:
                </Paragraph>
                <ul>
                  <li><Text strong>Standard Contractual Clauses (SCCs):</Text> We implement the European Commission's approved Standard Contractual Clauses for data transfers from the EEA to third countries.</li>
                  <li><Text strong>Data Processing Agreements:</Text> We establish data processing agreements with our service providers that include appropriate data protection provisions.</li>
                  <li><Text strong>Regional Data Storage:</Text> Where required, we offer regional data storage options to maintain data within specific geographic boundaries.</li>
                </ul>
                <Paragraph>
                  By using our services, you consent to the transfer of your information to countries outside your country of residence, including the United States, which may have different data protection rules than those of your country.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Data Retention</Title>
              <div className="policy-content">
                <Paragraph>
                  We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. The specific retention periods depend on:
                </Paragraph>
                <ul>
                  <li>The nature of the information</li>
                  <li>Why we collected and processed it</li>
                  <li>Legal, accounting, reporting, or regulatory requirements</li>
                  <li>Your relationship with Datacord (active customer vs. former customer)</li>
                </ul>
                <Paragraph>
                  For account information, we generally retain this data for the duration of your relationship with Datacord, plus a reasonable period afterward to address any follow-up queries or legal requirements. For integration configuration data and logs, we retain this information according to our data retention schedule or your specific contractual arrangements.
                </Paragraph>
                <Paragraph>
                  You can request deletion of your personal information at any time by contacting us at privacy@datacord.com. Note that we may not be able to delete certain information if retention is necessary for legitimate business or legal purposes.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Children's Privacy</Title>
              <div className="policy-content">
                <Paragraph>
                  Datacord's services are not directed to individuals under the age of 16, and we do not knowingly collect personal information from children. If we learn that we have collected personal information from a child under 16, we will take steps to delete this information as quickly as possible. If you believe that a child under 16 has provided us with personal information, please contact us at privacy@datacord.com.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Changes To This Policy</Title>
              <div className="policy-content">
                <Paragraph>
                  We may update this Privacy Policy periodically to reflect changes in our practices, technologies, legal requirements, and other factors. When we make changes, we will update the "Last Updated" date at the top of this Policy. For significant changes, we will provide notice through our website or by sending you an email notification.
                </Paragraph>
                <Paragraph>
                  We encourage you to review this Privacy Policy regularly to stay informed about our information practices. Your continued use of our services after any changes to this Privacy Policy constitutes your acceptance of the updated policy.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Contact Information</Title>
              <div className="policy-content">
                <Paragraph>
                  If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us at:
                </Paragraph>
                <Paragraph>
                  <Text strong>Datacord LLC</Text><br />
                  Email: privacy@datacord.com<br />
                  Address: 254 Chapman Rd, Ste 208 #16454, Newark, Delaware 19702, USA<br />
                  Phone: (619)405-3952
                </Paragraph>
                <Paragraph>
                  For EU/EEA residents, please note that Datacord's EU representative can be contacted at eu-representative@datacord.com.
                </Paragraph>
                <Paragraph>
                  We are committed to working with you to obtain a fair resolution of any complaint or concern about privacy. If, however, you believe that we have not been able to assist with your complaint or concern, you may have the right to lodge a complaint with the data protection authority in your country.
                </Paragraph>
              </div>
            </div>
          </Col>
        </Row>
        
        <Row className="privacy-policy-footer">
          <Col span={24}>
            <Title level={4}>Need More Information?</Title>
            <Paragraph>
              If you have specific questions about how we handle your data or need clarification on any part of this policy, please reach out to our privacy team at <a href="mailto:privacy@datacord.com">privacy@datacord.com</a>.
            </Paragraph>
          </Col>
        </Row>
      </div>
  )
}

export default PrivacyPolicyContent
